/**
 * SubjectsPage - Страница управления предметами для мастер-классов
 *
 * Доступ: Admin, Manager
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Title,
  Button,
  Group,
  Table,
  Text,
  Badge,
  ActionIcon,
  Menu,
  Loader,
  Center,
  Box,
  Stack,
} from '@mantine/core';
import {
  IconPlus,
  IconDots,
  IconEye,
  IconPencil,
  IconTrash,
  IconBook,
  IconCheck,
  IconX,
} from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import SubjectFormModal from '../components/Subjects/SubjectFormModal';
import SubjectViewModal from '../components/Subjects/SubjectViewModal';
import tokens from '../theme/designTokens';

/**
 * API функции для предметов
 */
const subjectsApi = {
  async getSubjects(includeInactive = true) {
    const response = await fetch(`/api/subjects?include_inactive=${includeInactive}`, {
      credentials: 'include',
    });
    if (!response.ok) {
      throw new Error('Не удалось загрузить предметы');
    }
    return response.json();
  },

  async deleteSubject(id) {
    const response = await fetch(`/api/subjects/${id}`, {
      method: 'DELETE',
      credentials: 'include',
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Не удалось удалить предмет');
    }
  },
};

/**
 * Форматирование даты
 */
const formatDate = (dateString) => {
  const date = new Date(dateString);
  return date.toLocaleString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'Europe/Moscow',
  });
};

const SubjectsPage = () => {
  const [subjects, setSubjects] = useState([]);
  const [loading, setLoading] = useState(true);

  // Модальное окно формы
  const [formModalOpen, setFormModalOpen] = useState(false);
  const [editingSubject, setEditingSubject] = useState(null);

  // Модальное окно просмотра
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [viewingSubject, setViewingSubject] = useState(null);

  // Загрузка предметов
  const loadSubjects = useCallback(async () => {
    try {
      setLoading(true);
      const data = await subjectsApi.getSubjects(true);
      setSubjects(data || []);
    } catch (err) {
      console.error('Failed to load subjects:', err);
      notifications.show({
        title: 'Ошибка',
        message: err.message,
        color: 'red',
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSubjects();
  }, [loadSubjects]);

  // Открыть форму создания
  const handleCreate = () => {
    setEditingSubject(null);
    setFormModalOpen(true);
  };

  // Открыть форму редактирования
  const handleEdit = (subject) => {
    setEditingSubject(subject);
    setFormModalOpen(true);
  };

  // Открыть просмотр
  const handleView = (subject) => {
    setViewingSubject(subject);
    setViewModalOpen(true);
  };

  // Удалить предмет
  const handleDelete = async (subject) => {
    if (!window.confirm(`Удалить предмет "${subject.name}"?`)) {
      return;
    }

    try {
      await subjectsApi.deleteSubject(subject.id);
      notifications.show({
        title: 'Успешно',
        message: 'Предмет удалён',
        color: 'green',
        icon: <IconCheck size={16} />,
      });
      await loadSubjects();
    } catch (err) {
      console.error('Failed to delete subject:', err);
      notifications.show({
        title: 'Ошибка',
        message: err.message,
        color: 'red',
      });
    }
  };

  // После сохранения формы
  const handleFormSuccess = () => {
    setFormModalOpen(false);
    setEditingSubject(null);
    loadSubjects();
  };

  // Редактирование из модалки просмотра
  const handleEditFromView = (subject) => {
    setViewModalOpen(false);
    setViewingSubject(null);
    setEditingSubject(subject);
    setFormModalOpen(true);
  };

  return (
    <Box px="md" py="md">
      <Stack gap="md">
        {/* Заголовок */}
        <Group justify="space-between" align="flex-end">
          <Box>
            <Title order={2} style={{ color: tokens.colors.brand.primary }}>
              Предметы
            </Title>
            <Text c="dimmed" size="sm">
              Предметы для мастер-классов (Робототехника, Программирование и др.)
            </Text>
          </Box>
          <Button
            leftSection={<IconPlus size={16} />}
            onClick={handleCreate}
          >
            Добавить предмет
          </Button>
        </Group>

        {/* Таблица */}
        {loading ? (
          <Center py="xl">
            <Loader size="md" />
          </Center>
        ) : subjects.length === 0 ? (
          <Center py="xl">
            <Box style={{ textAlign: 'center' }}>
              <IconBook
                size={48}
                color={tokens.colors.neutral[4]}
                style={{ marginBottom: '16px' }}
              />
              <Text size="lg" c="dimmed" mb="xs">
                Предметы не найдены
              </Text>
              <Text size="sm" c="dimmed" mb="md">
                Создайте предметы для использования в мастер-классах
              </Text>
              <Button
                variant="light"
                leftSection={<IconPlus size={16} />}
                onClick={handleCreate}
              >
                Добавить предмет
              </Button>
            </Box>
          </Center>
        ) : (
          <Table striped highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th style={{ width: 60 }}>ID</Table.Th>
                <Table.Th style={{ width: 250 }}>Название</Table.Th>
                <Table.Th>Описание</Table.Th>
                <Table.Th style={{ width: 100 }}>Статус</Table.Th>
                <Table.Th style={{ width: 180 }}>Создан</Table.Th>
                <Table.Th style={{ width: 60 }}>Действия</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {subjects.map((subject) => (
                <Table.Tr
                  key={subject.id}
                  style={{ cursor: 'pointer' }}
                  onClick={() => handleView(subject)}
                >
                  <Table.Td>
                    <Text size="sm" c="dimmed">#{subject.id}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Group gap="xs">
                      <IconBook size={16} color={tokens.colors.brand.primary} />
                      <Text size="sm" fw={500}>{subject.name}</Text>
                    </Group>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm" c="dimmed" lineClamp={2}>
                      {subject.description || '—'}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    {subject.is_active ? (
                      <Badge color="green" variant="light" radius="xl" size="sm">
                        Активен
                      </Badge>
                    ) : (
                      <Badge color="gray" variant="light" radius="xl" size="sm">
                        Неактивен
                      </Badge>
                    )}
                  </Table.Td>
                  <Table.Td>
                    <Text size="xs" c="dimmed">
                      {formatDate(subject.created_at)}
                    </Text>
                    {subject.created_by_name && (
                      <Text size="xs" c="dimmed">
                        {subject.created_by_name}
                      </Text>
                    )}
                  </Table.Td>
                  <Table.Td>
                    <Menu position="bottom-end" withinPortal>
                      <Menu.Target>
                        <ActionIcon
                          variant="subtle"
                          color="gray"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <IconDots size={16} />
                        </ActionIcon>
                      </Menu.Target>
                      <Menu.Dropdown onClick={(e) => e.stopPropagation()}>
                        <Menu.Item
                          leftSection={<IconEye size={14} />}
                          onClick={() => handleView(subject)}
                        >
                          Просмотр
                        </Menu.Item>
                        <Menu.Item
                          leftSection={<IconPencil size={14} />}
                          onClick={() => handleEdit(subject)}
                        >
                          Редактировать
                        </Menu.Item>
                        <Menu.Divider />
                        <Menu.Item
                          leftSection={<IconTrash size={14} />}
                          color="red"
                          onClick={() => handleDelete(subject)}
                        >
                          Удалить
                        </Menu.Item>
                      </Menu.Dropdown>
                    </Menu>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        )}
      </Stack>

      {/* Модальное окно формы */}
      <SubjectFormModal
        opened={formModalOpen}
        onClose={() => {
          setFormModalOpen(false);
          setEditingSubject(null);
        }}
        onSuccess={handleFormSuccess}
        subject={editingSubject}
      />

      {/* Модальное окно просмотра */}
      <SubjectViewModal
        opened={viewModalOpen}
        onClose={() => {
          setViewModalOpen(false);
          setViewingSubject(null);
        }}
        subject={viewingSubject}
        onEdit={handleEditFromView}
      />
    </Box>
  );
};

export default SubjectsPage;
